import { type FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';
import { Form } from 'react-bootstrap';
import { debounce } from 'lodash-es';

import { type RootState } from '@editor2/reducers/root';
import {
	editElementsCollapseState,
	moveElement,
	updatePage
} from '@editor2/reducers/editor/editorSlice';
import HtmlElement from '@editor2/containers/PageElementsPage/components/HtmlElement';
import FigureElement from '@editor2/containers/PageElementsPage/components/FigureElement';
import { contentBlock, contentTabStyle } from '@editor2/containers/PageElementsPage/styles';
import { type DoubleElements, type PageElement, ElementType } from '@editor2/types/pageElements';
import Tools from './Tools';
import ElementWrapper from './ElementWrapper';
import AddElementsBlock from './AddElementsBlock';
import ChartElement from './ChartElement';
import McQuestionElement from './MCQuestion';
import SaQuestionElement from './SAQuestion';
import ResponseBoardElement from './ResponseBoard';
import PollElement from './PollElement';
import ArtifactElement from './ArtifactElement';
import JsonConfig from './JsonConfig';
import NestingInstructionElement from './NestingInstruction';
import DecisionElement from './Decision';
import GeoMapElement from './GeoMap';
import { showToast } from '@editor2/reducers/toastSlice';
import { ToastSeverity } from '@editor2/types/toastSeverity';
import VoiceRecordingElement from './VoiceRecording';
import PageProgressElement from './PageProgress';
import {
	validateDoubleNesting,
	validateDirectionOrder,
	validateAllowedElementsInside,
	validateIfInsideDouble
} from '@editor2/services/doubleElementValidationsService';
import { isDoubleElement } from '@editor2/services/elementService';
import { CollapseState } from '@editor2/types/collapseState';
import TimelineElement from './TimelineElement';
import ExternalTool from './ExternalTool';
import MCQuestionPoolElement from './MCQuestionPool';
import PollResultsElement from './PollResults';
import QuestionDeckElement from './QuestionDeck';
import AiChatboxElement from './AiChatboxElement';
import EocCheckinChatbotElement from './EocCheckinChatbot';
import CarouselElement from './Carousel';

interface Props {
	pageId: string;
	courseId: number;
}

const TableOfContents: FC<Props> = (props) => {
	const { pageId, courseId } = props;

	const dispatch = useDispatch();

	const { pages, elements } = useSelector((state: RootState) => state.courses[courseId]);

	const page = pages.entities[pageId];
	const pageElementsIds = page.elements;

	const [editorialNotes, setEditorialNotes] = useState(page.notes);

	useEffect(() => {
		if (editorialNotes !== page.notes) setEditorialNotes(page.notes);
	}, [page.notes]);

	useEffect(() => {
		dispatch(editElementsCollapseState({ collapseState: CollapseState.CollapseAll, courseId }));
	}, [dispatch, courseId]);

	const debouncedDispatch = useCallback(
		debounce((action) => {
			dispatch(action);
		}, 500),
		[]
	);

	const onChange = (value) => {
		setEditorialNotes(value.notes);
		debouncedDispatch(updatePage({ pageId: page.family_id, courseId, ...value }));
	};

	const showWarningToast = (message: string) => {
		dispatch(
			showToast({
				severity: ToastSeverity.Warning,
				message
			})
		);
	};

	const onDragEnd = (result) => {
		const { source, destination, draggableId, type } = result;
		const { droppableId: sourceDroppableId, index: sourceIndex } = source;
		const { droppableId: destinationDroppableId, index: destinationIndex } = destination;

		const element = elements.entities[draggableId];

		if (sourceDroppableId !== destinationDroppableId) return;

		try {
			const validatePayload = {
				draggableElement: element as DoubleElements,
				pageElements: pages.entities[sourceDroppableId].elements,
				sourceIndex,
				destinationIndex,
				elements
			};

			if (isDoubleElement(element.type)) {
				validateDoubleNesting(validatePayload);

				validateDirectionOrder(validatePayload);

				validateAllowedElementsInside(validatePayload);
			} else {
				const pageElements = pages.entities[sourceDroppableId].elements;
				const payload = { pageElements, sourceIndex, destinationIndex, elements };
				validateIfInsideDouble(payload);
			}
		} catch (error) {
			showWarningToast(error.message);
			return;
		}

		if (type === 'Element') {
			dispatch(
				moveElement({
					sourceDroppableId,
					destinationDroppableId,
					destinationIndex,
					elementId: draggableId,
					courseId
				})
			);
		}
	};

	// eslint-disable-next-line complexity
	const getElementComponent = (element: PageElement, index: number) => {
		switch (element.type) {
			case ElementType.HTMLText:
				return (
					<HtmlElement
						element={element}
						key={element.family_id}
						courseId={courseId}
						index={index}
					/>
				);
			case ElementType.Figure:
				return <FigureElement element={element} key={element.family_id} courseId={courseId} />;
			case ElementType.GeoMap:
				return <GeoMapElement element={element} key={element.family_id} courseId={courseId} />;
			case ElementType.Chart:
				return (
					<ChartElement
						element={element}
						key={element.family_id}
						courseId={courseId}
						pageType={page.page_type}
					/>
				);
			case ElementType.MCQuestion:
				return <McQuestionElement element={element} key={element.family_id} courseId={courseId} />;
			case ElementType.MCQuestionPool:
				return (
					<MCQuestionPoolElement element={element} key={element.family_id} courseId={courseId} />
				);
			case ElementType.SAQuestion:
				return (
					<SaQuestionElement
						element={element}
						key={element.family_id}
						courseId={courseId}
						pageType={page.page_type}
					/>
				);
			case ElementType.YourTurn:
				return (
					<ResponseBoardElement
						element={element}
						key={element.family_id}
						courseId={courseId}
						pageType={page.page_type}
					/>
				);

			case ElementType.Poll:
				return <PollElement element={element} key={element.family_id} courseId={courseId} />;
			case ElementType.PollResults:
				return <PollResultsElement element={element} key={element.family_id} courseId={courseId} />;

			case ElementType.ArtifactArticle:
			case ElementType.ArtifactAudio:
			case ElementType.ArtifactVideo:
			case ElementType.ArtifactDownload:
			case ElementType.ArtifactPdfDownload:
			case ElementType.ArtifactDocxDownload:
			case ElementType.ArtifactXlsxDownload:
				return <ArtifactElement element={element} key={element.family_id} courseId={courseId} />;

			case ElementType.Builder:
			case ElementType.PopupQuiz:
				return <JsonConfig element={element} key={element.family_id} courseId={courseId} />;

			case ElementType.ExternalTool:
				return <ExternalTool element={element} key={element.family_id} courseId={courseId} />;

			case ElementType.NestingInstruction:
				return element.direction === 'in' ? (
					<NestingInstructionElement
						element={element}
						key={element.family_id}
						courseId={courseId}
					/>
				) : (
					<></>
				);

			case ElementType.QuestionDeck:
				return element.direction === 'in' ? (
					<QuestionDeckElement
						element={element}
						key={element.family_id}
						courseId={courseId}
						pageId={pageId}
						index={index}
					/>
				) : (
					<></>
				);

			case ElementType.Decision:
				return <DecisionElement element={element} key={element.family_id} courseId={courseId} />;

			case ElementType.VoiceRecording:
				return (
					<VoiceRecordingElement element={element} key={element.family_id} courseId={courseId} />
				);

			case ElementType.PageProgress:
				return (
					<PageProgressElement element={element} key={element.family_id} courseId={courseId} />
				);

			case ElementType.Timeline:
				return <TimelineElement element={element} key={element.family_id} courseId={courseId} />;
			case ElementType.AiChatbox:
				return <AiChatboxElement element={element} key={element.family_id} courseId={courseId} />;
			case ElementType.EocCheckinChatbot:
				return (
					<EocCheckinChatbotElement
						element={element}
						key={element.family_id}
						courseId={courseId}
						pageId={pageId}
					/>
				);
			case ElementType.Carousel:
				return <CarouselElement element={element} key={element.family_id} courseId={courseId} />;

			default:
				return <></>;
		}
	};

	return (
		<div className={contentTabStyle}>
			<Form.Group>
				<Form.Label>Editorial notes</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					value={editorialNotes}
					onChange={(e) => onChange({ notes: e.target.value })}
				/>
			</Form.Group>

			<AddElementsBlock courseId={courseId} pageId={pageId} />
			<Tools courseId={courseId} />

			<div className={contentBlock}>
				<DragDropContext onDragEnd={onDragEnd}>
					<DelayedDroppableWrapper droppableId={pageId} type="Element">
						{(provided) => (
							<div ref={provided.innerRef} {...provided.droppableProps}>
								{pageElementsIds.map((elementId, i) => {
									const element = elements.entities[elementId];

									return (
										<ElementWrapper
											courseId={courseId}
											pageId={pageId}
											element={element}
											index={i}
											key={element.family_id}>
											{getElementComponent(element, i)}
										</ElementWrapper>
									);
								})}
								{provided.placeholder}
							</div>
						)}
					</DelayedDroppableWrapper>
				</DragDropContext>
			</div>
		</div>
	);
};

export default TableOfContents;
